<template>
  <div
    id="invoiceList"
    class="mainCard"
  >
    <div class="filter">
      <el-input
        v-model="filterText"
        class="filterText clearInput"
        :placeholder="$t('common.search')"
        @change="search"
      >
        <el-button
          slot="append"
          icon="el-icon-search"
          @click="search"
        />
      </el-input>

      <el-select
        v-model="filterSelect"
        :placeholder="$t('common.filterBy')"
        value=""
        @change="search"
      >
        <el-option
          :label="$t('common.all')"
          value=""
        />
        <el-option
          :label="$t('coach.invoice.detail.paymentCompleted')"
          value="completed"
        />
        <el-option
          :label="$t('coach.invoice.detail.paymentSent')"
          value="payment_sent"
        />
        <el-option
          :label="$t('coach.invoice.detail.paymentPending')"
          value="pending"
        />
        <el-option
          :label="$t('coach.invoice.detail.paymentDraft')"
          value="draft"
        />
      </el-select>
    </div>

    <div class="guideToGcg">
      <i18n path="coach.invoice.engagementTerm">
        <template #visit>
          <a
            class="coloredAnchor"
            href="https://info.gcgroup.io/platform/payouts/"
            target="_blank"
          >{{ $t('coach.invoice.visit') }}</a>
        </template>
      </i18n>
    </div>

    <el-table
      ref="invoiceTable"
      v-loading="loading"
      :data="invoices"
      class="clear-table invoiceTable"
      :empty-text="$t('coach.invoice.noData')"
    >
      <el-table-column
        prop="created_at"
        :label="$t('coach.invoice.detail.invoiceDate')"
        :min-width="130"
      >
        <template slot-scope="date">
          <span>
            {{ $moment(date.row.invoice_date).format('YYYY-MM-DD') }}
          </span>
        </template>
      </el-table-column>

      <el-table-column
        prop="id"
        :label="$t('coach.invoice.detail.ceNumber')"
        :min-width="170"
      />

      <el-table-column
        prop="status"
        :label="$t('coach.invoice.detail.paymentStatus')"
        align="center"
        :min-width="180"
      >
        <template slot-scope="status">
          <span class="status">
            <template v-if="['pending', 'completed'].includes(status.row.status)">
              {{ $t('coach.invoice.detail.payment') }}
              {{ status.row.status }}
            </template>
            <template v-else>
              {{ status.row.status ? status.row.status.replace('_', ' ') : '-' }}
            </template>
          </span>
          <div v-if="status.row.status === 'payment_sent'">
            <el-button
              class="confirmBtn"
              type="primary"
              size="mini"
              round
              @click="confirmPayment(status.row.id)"
            >
              {{ $t('coach.invoice.confirmReceived') }}
            </el-button>
          </div>
        </template>
      </el-table-column>

      <el-table-column
        prop="button"
        :width="120"
        align="center"
      >
        <template slot-scope="report">
          <span
            v-if="($moment(report.row.created_at).add(60, 'days').isBefore($moment()))"
            class="viewBtn"
            @click="viewInvoice(report.row.id)"
          >
            {{ $t('coach.invoice.detail.viewInvoices') }}
          </span>
          <span
            v-else
            class="viewBtn"
            @click="viewReport(report.row.id)"
          >
            {{ $t('settings.viewDetails') }}
          </span>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      v-if="count"
      layout="prev, pager, next"
      :total="count"
      :pager-count="5"
      :current-page.sync="currentPage"
      @current-change="loadInvoices"
    />

    <InvoiceModal
      :show.sync="showInvoice"
      :modal-type.sync="modalType"
      :selected-id.sync="selectedId"
      :role="user.user.role_type"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import api from '@/api';
import InvoiceModal from './widget/InvoiceModal.vue';

export default {
  components: {
    InvoiceModal,
  },

  data: () => ({
    showInvoice: false,
    modalType: null,
    selectedId: null,
    loading: false,
    filterText: '',
    filterSelect: '',
    invoices: [],
    currentPage: 1,
    count: 0,
  }),

  computed: {
    ...mapState({
      user: (state) => state.user,
    }),
  },

  async created() {
    this.loadInvoices();
  },

  methods: {
    search() {
      this.currentPage = 1; // Reset to first page
      this.loadInvoices();
    },

    async loadInvoices() {
      try {
        this.loading = true;
        let response = null;
        const params = {
          page: this.currentPage,
        };

        if (this.filterSelect) {
          params.status = this.filterSelect;
        }

        if (this.filterText) {
          params.search = this.filterText;
        }

        response = await api.getCoachInvoices(params);

        // Set response data
        if (response) {
          this.invoices = response.data.results.sort((a, b) => {
            // Redundant code but it makes code not flood over the max line
            const compare = this.$moment(b.created_at) - this.$moment(a.created_at);
            return compare;
          });
          this.count = response.data.count;
        }
      } catch (err) {
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.loading = false;
      }
    },

    confirmPayment(id) {
      this.$confirm(`${this.$t('coach.invoice.confirmPayment')}`, '', {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.goBack'),
        type: 'warning',
      }).then(async () => {
        try {
          await api.confirmCoachInvoices(id);
          this.$message.success(this.$t('coach.invoice.confirmPaymentSaved'));
          this.loadInvoices();
        } catch (err) {
          console.log(err);
          if (err.msg) {
            this.$message.error(err.msg);
          }
        }
      }).catch((err) => {
        if (err !== 'cancel') {
          this.$message.error(err.msg);
          console.log(err);
        }
      });
    },

    viewReport(id) {
      this.modalType = 'coach';
      this.selectedId = id;
      this.showInvoice = true;
    },

    viewInvoice(id) {
      const routeData = this.$router.resolve({ name: 'invoiceReportAdmin', params: { id } });
      window.open(routeData.href, '_blank');
    },
  },
};
</script>

<style lang="scss" scoped>
#invoiceList {
  .filter {
    margin: 4px auto;
    text-align: right;

    .filterText {
      height: 30px;
      width: 54%;
      margin: 0 3% 0 0;
      max-width: 250px;
      border-bottom: 1px solid var(--themeFontColor);
    }

    .el-select {
      width: 170px;
      max-width: 40%;
    }
  }

  .guideToGcg {
    color: var(--themeFontColor);
    padding: 10px 0;

    .coloredAnchor {
      color: var(--themeFontColor);
    }
  }

  .invoiceTable {
    width: 100%;

    .status {
      text-transform: capitalize;
    }

    .confirmBtn {
      margin-top: 5px;
      padding: 7px 4px;
      width: 90%;
      word-break: break-word;
      white-space: normal;
    }

    .viewBtn {
      cursor: pointer;
      text-decoration: underline;
    }
  }
}
</style>

<style lang="scss">
#invoiceList {
  .filter {
    .el-select {
      .el-input__inner {
        border-radius: 20px;
        background-color: transparent;
        color: var(--themeFontColor);
        border-color: var(--themeFontColor);
      }
    }
  }

  .invoiceTable {
    .el-table__expand-icon {
      color: var(--themeFontColor);
    }

    tr {
      th:first-child,
      td:first-child {
        padding-left: 15px;
      }
    }
  }
}
</style>
